import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup
} from 'react-bootstrap';
import { FaEye, FaHeart } from 'react-icons/fa';

import { useParams, Link } from 'react-router-dom';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';
import QuickViewModal from '../components/QuickViewModal';
import StarRating from '../components/product/StarRating';
import { useProductRatings } from '../hooks/useProductRatings';

const SORT_OPTIONS = [
  { key: 'featured', label: '✨ Featured' },
  { key: 'priceLow', label: '₹ Price: Low to High' },
  { key: 'priceHigh', label: '₹ Price: High to Low' },
  { key: 'rating', label: '⭐ Top Rated' },
  { key: 'newest', label: '🆕 Newest First' },
  { key: 'name', label: '🔤 Name (A–Z)' }
];

function CategoryProductsPage() {
  const { category } = useParams();

  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const [search, setSearch] = useState('');

  const [sortBy, setSortBy] = useState('featured');

  const [inStockOnly, setInStockOnly] = useState(false);

  /* Products already sent to the wishlist this visit — keeps the heart filled */
  const [wishlisted, setWishlisted] = useState([]);

  /* Real customer ratings — products with no reviews report 0 */
  const { ratings, getRating } = useProductRatings();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      try {
        const { data } = await axios.get(
          `${API}/products/category/${category}`
        );

        setProducts(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  /* Reset the toolbar when the visitor switches category */
  useEffect(() => {
    setSearch('');
    setSortBy('featured');
    setInStockOnly(false);
  }, [category]);

  const addToWishlist = async (product) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));

    if (!userInfo) {
      toast.error('Please Login First');

      return;
    }

    try {
      await axios.post(`${API}/wishlist`, {
        userId: userInfo._id,

        productId: product._id,

        name: product.name,

        image: product.image,

        price: product.price
      });

      setWishlisted((prev) => [...prev, product._id]);

      toast.success('Added To Wishlist ❤️');
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || 'Failed To Add Wishlist');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSortBy('featured');
    setInStockOnly(false);
  };

  /* ---- Derived data for the hero stats ---- */
  const inStockCount = products.filter((p) => p.stock > 0).length;

  const lowestPrice = products.length
    ? Math.min(...products.map((p) => p.price))
    : 0;

  /* ---- Search → stock filter → sort ---- */
  const keyword = search.trim().toLowerCase();

  const visibleProducts = products
    .filter((product) => {
      const matchesSearch =
        !keyword ||
        product.name?.toLowerCase().includes(keyword) ||
        product.shortDescription?.toLowerCase().includes(keyword);

      const matchesStock = !inStockOnly || product.stock > 0;

      return matchesSearch && matchesStock;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'priceLow':
          return a.price - b.price;

        case 'priceHigh':
          return b.price - a.price;

        case 'rating':
          return (
            (ratings[b._id]?.averageRating || 0) -
            (ratings[a._id]?.averageRating || 0)
          );

        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);

        case 'name':
          return (a.name || '').localeCompare(b.name || '');

        default:
          /* Featured: best sellers first, then in-stock ahead of sold out */
          if (Boolean(b.topSale) !== Boolean(a.topSale)) {
            return b.topSale ? 1 : -1;
          }

          return (b.stock > 0) - (a.stock > 0);
      }
    });

  const isFiltered = Boolean(keyword) || inStockOnly || sortBy !== 'featured';

  return (
    <>
      <CustomNavbar />

      <div className="cp-wrap">
        <Container>
          {/* Breadcrumb */}
          <div className="cp-crumbs mb-3">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/categories">Categories</Link>
            <span>›</span>
            <strong className="text-capitalize">{category}</strong>
          </div>

          {/* Premium Header Card */}
          <Card className="border-0 shadow-lg mb-4 cp-hero">
            <span className="cp-hero-blob cp-hero-blob-1" />
            <span className="cp-hero-blob cp-hero-blob-2" />
            <span className="cp-hero-grid" />

            {/* Drifting decorations — the bag and diamond were removed */}
            <span className="cp-hero-float cp-hero-float-1">✨</span>

            {/* Toran — a marigold garland strung across the top, swaying */}
            <span className="cp-toran" aria-hidden="true">
              {Array.from({ length: 15 }).map((_, i) => (
                <span
                  className="cp-toran-hang"
                  key={i}
                  style={{
                    /* dip the middle of the garland to follow the thread */
                    marginTop: `${Math.round(Math.sin((i / 14) * Math.PI) * 11)}px`,
                    animationDelay: `${(i * 0.17).toFixed(2)}s`
                  }}
                >
                  {i % 2 === 0 ? '🌼' : '🍃'}
                </span>
              ))}
            </span>

            {/* Temple bell that rings every few seconds */}
            <span className="cp-bell" aria-hidden="true">
              🔔
            </span>

            {/* Festival string lights chasing along the base */}
            <span className="cp-lights" aria-hidden="true">
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  className="cp-light"
                  key={i}
                  style={{ animationDelay: `${(i * 0.11).toFixed(2)}s` }}
                />
              ))}
            </span>

            <Card.Body className="text-center position-relative py-5">
              <span className="cp-hero-eyebrow">Curated Collection</span>

              <h1 className="fw-bold text-capitalize mb-3 cp-hero-title">
                {category}
              </h1>

              <p className="mb-4 cp-hero-text">
                Discover premium products, exclusive offers and trending
                collections in this category.
              </p>

              <div className="d-flex justify-content-center gap-2 gap-md-3 flex-wrap">
                <span className="cp-chip cp-chip-gold">
                  📦 {loading ? '…' : products.length} Products
                </span>

                {!loading && inStockCount > 0 && (
                  <span className="cp-chip cp-chip-green">
                    ✅ {inStockCount} In Stock
                  </span>
                )}

                {!loading && lowestPrice > 0 && (
                  <span className="cp-chip cp-chip-light">
                    🏷 From ₹{lowestPrice.toLocaleString()}
                  </span>
                )}

                <span className="cp-chip cp-chip-light">🚚 Free Delivery</span>
              </div>
            </Card.Body>
          </Card>

          {/* Toolbar — search, sort, stock filter */}
          {!loading && products.length > 0 && (
            <Card className="border-0 shadow-sm mb-4 cp-toolbar">
              <Card.Body className="p-3">
                <Row className="g-2 align-items-center">
                  <Col lg={5} md={12}>
                    <InputGroup className="cp-search">
                      <InputGroup.Text className="bg-white border-0">
                        🔍
                      </InputGroup.Text>
                      <Form.Control
                        className="border-0"
                        placeholder={`Search in ${category}...`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <Button
                          variant="link"
                          className="cp-search-clear"
                          onClick={() => setSearch('')}
                        >
                          ✕
                        </Button>
                      )}
                    </InputGroup>
                  </Col>

                  <Col lg={4} md={7} xs={8}>
                    <Form.Select
                      className="cp-sort"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col lg={3} md={5} xs={4}>
                    <Button
                      className={`w-100 fw-semibold cp-stock-toggle${
                        inStockOnly ? ' is-on' : ''
                      }`}
                      onClick={() => setInStockOnly((prev) => !prev)}
                    >
                      {inStockOnly ? '✅ In Stock' : '☐ In Stock'}
                    </Button>
                  </Col>
                </Row>

                {isFiltered && (
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-3 cp-toolbar-foot">
                    <small className="text-muted">
                      Showing <strong>{visibleProducts.length}</strong> of{' '}
                      {products.length} products
                    </small>

                    <Button
                      size="sm"
                      variant="link"
                      className="cp-reset"
                      onClick={clearFilters}
                    >
                      ↺ Reset filters
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {loading ? (
            <Row className="g-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Col xl={3} lg={4} md={6} sm={6} xs={12} key={i}>
                  <Card className="border-0 shadow-sm h-100 cp-card cp-skel-card">
                    <div className="cp-skel cp-skel-img" />
                    <Card.Body className="p-3">
                      <div className="cp-skel cp-skel-line cp-skel-stars" />
                      <div className="cp-skel cp-skel-line cp-skel-title" />
                      <div className="cp-skel cp-skel-line cp-skel-desc" />
                      <div className="cp-skel cp-skel-line cp-skel-price" />
                      <div className="cp-skel cp-skel-line cp-skel-btn" />
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : visibleProducts.length === 0 ? (
            <Card className="border-0 shadow-lg text-center p-5 cp-empty">
              <div className="cp-empty-icon">{products.length ? '🔍' : '😔'}</div>

              <h3 className="fw-bold mt-2">
                {products.length ? 'No Matching Products' : 'No Products Found'}
              </h3>

              <p className="text-muted mb-3">
                {products.length
                  ? 'Try a different search, or reset the filters.'
                  : 'Products will appear here soon.'}
              </p>

              {products.length ? (
                <div>
                  <Button className="cp-cta px-4" onClick={clearFilters}>
                    ↺ Reset Filters
                  </Button>
                </div>
              ) : (
                <div>
                  <Button as={Link} to="/categories" className="cp-cta px-4">
                    Browse Other Categories →
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <Row className="g-4">
              {visibleProducts.map((product, index) => {
                const oldPrice = Math.round(product.price * 1.2);
                const discount = Math.round(
                  ((oldPrice - product.price) / oldPrice) * 100
                );

                const soldOut = product.stock <= 0;
                const lowStock = !soldOut && product.stock <= 5;

                /* Unreviewed products report a 0 rating */
                const rating = getRating(product._id);

                return (
                  <Col xl={3} lg={4} md={6} sm={6} xs={12} key={product._id}>
                    <Card
                      className={`border-0 shadow-sm h-100 cp-card cp-fade-in${
                        soldOut ? ' cp-card-out' : ''
                      }`}
                      /* Staggered entrance — cards cascade in as you scroll */
                      style={{
                        animationDelay: `${Math.min(index, 11) * 60}ms`
                      }}
                    >
                      <div className="cp-img-wrap">
                        {product.topSale && (
                          <span className="cp-badge cp-trending">
                            🔥 Best Seller
                          </span>
                        )}

                        {discount > 0 && (
                          <span className="cp-badge cp-discount">
                            -{discount}%
                          </span>
                        )}

                        <Card.Img
                          src={product.image}
                          alt={product.name}
                          className="cp-img"
                        />

                        <span className="cp-shine" />

                        {soldOut && (
                          <span className="cp-soldout">Out of Stock</span>
                        )}

                        <button
                          type="button"
                          className={`cp-wish${
                            wishlisted.includes(product._id) ? ' is-on' : ''
                          }`}
                          title="Add to wishlist"
                          onClick={() => addToWishlist(product)}
                        >
                          <FaHeart />
                        </button>

                        <button
                          type="button"
                          className="cp-overlay"
                          onClick={() => setQuickViewProduct(product)}
                        >
                          <span className="cp-overlay-btn">
                            <FaEye className="me-1" /> Quick View
                          </span>
                        </button>
                      </div>

                      <Card.Body className="d-flex flex-column p-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <StarRating
                            average={rating.averageRating}
                            count={rating.totalReviews}
                          />

                          {lowStock && (
                            <small className="cp-low">
                              Only {product.stock} left
                            </small>
                          )}
                        </div>

                        <Card.Title className="fw-bold cp-name mb-1">
                          {product.name}
                        </Card.Title>

                        <Card.Text className="text-muted small cp-desc mb-2">
                          {product.shortDescription}
                        </Card.Text>

                        <div className="d-flex align-items-baseline gap-2 mb-1">
                          <span className="fw-bold cp-price">
                            ₹{product.price.toLocaleString()}
                          </span>
                          <span className="text-muted text-decoration-line-through small">
                            ₹{oldPrice.toLocaleString()}
                          </span>
                        </div>

                        {discount > 0 && (
                          <small className="cp-save mb-3">
                            You save ₹{(oldPrice - product.price).toLocaleString()}
                          </small>
                        )}

                        <Button
                          as={Link}
                          to={`/product/${product._id}`}
                          className="mt-auto w-100 fw-bold cp-btn"
                        >
                          View Details <span className="cp-btn-arrow">→</span>
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        show={!!quickViewProduct}
        onHide={() => setQuickViewProduct(null)}
      />

      <style>{categoryProductsStyles}</style>
    </>
  );
}

const categoryProductsStyles = `
  .cp-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(32px, 6vw, 60px) 0;
  }

  /* Breadcrumb */
  .cp-crumbs {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.86rem;
    color: #9a3412;
  }
  .cp-crumbs a {
    color: #9a3412;
    text-decoration: none;
    opacity: 0.75;
    transition: opacity 0.2s ease;
  }
  .cp-crumbs a:hover { opacity: 1; text-decoration: underline; }
  .cp-crumbs span { color: #d1a06a; }

  /* ===== Hero ===== */
  .cp-hero {
    border-radius: 30px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #7c2d12, #c2410c 45%, #ea580c 75%, #f59e0b);
    background-size: 220% 220%;
    animation: cpHeroDrift 14s ease-in-out infinite;
    position: relative;
  }
  @keyframes cpHeroDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  .cp-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
  }
  .cp-hero-blob-1 {
    width: 220px; height: 220px;
    background: #fbbf24;
    top: -90px; left: 12%;
    animation: cpBlob 9s ease-in-out infinite;
  }
  .cp-hero-blob-2 {
    width: 200px; height: 200px;
    background: #38bdf8;
    bottom: -90px; right: 10%;
    opacity: 0.35;
    animation: cpBlob 11s ease-in-out infinite reverse;
  }
  @keyframes cpBlob {
    0%, 100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(24px, 16px) scale(1.15); }
  }
  /* Faint grid for depth */
  .cp-hero-grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px);
    background-size: 46px 46px;
    -webkit-mask-image: radial-gradient(circle at 50% 40%, #000, transparent 72%);
    mask-image: radial-gradient(circle at 50% 40%, #000, transparent 72%);
    pointer-events: none;
  }
  .cp-hero-float {
    position: absolute;
    font-size: 1.5rem;
    opacity: 0.55;
    pointer-events: none;
    animation: cpFloat 7s ease-in-out infinite;
  }
  .cp-hero-float-1 { top: 22%; left: 9%; animation-delay: -1s; }
  @keyframes cpFloat {
    0%, 100% { transform: translateY(0) rotate(-8deg); opacity: 0.35; }
    50%      { transform: translateY(-18px) rotate(8deg); opacity: 0.8; }
  }

  /* ===== Divine trimmings ===== */

  /* Toran garland — pendants hang from a curved thread and sway */
  .cp-toran {
    position: absolute;
    top: 0; left: 0; right: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0 18px;
    z-index: 2;
    pointer-events: none;
  }
  .cp-toran::before {
    content: '';
    position: absolute;
    left: 4%; right: 4%;
    top: -16px;
    height: 30px;
    border-bottom: 2px solid rgba(253, 230, 138, 0.5);
    border-radius: 0 0 50% 50% / 0 0 100% 100%;
  }
  .cp-toran-hang {
    font-size: 0.95rem;
    line-height: 1;
    transform-origin: 50% -6px;
    filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.28));
    animation: cpSway 3.4s ease-in-out infinite;
  }
  @keyframes cpSway {
    0%, 100% { transform: rotate(-8deg); }
    50%      { transform: rotate(8deg); }
  }

  /* Temple bell — still, then a burst of rings that damp out */
  .cp-bell {
    position: absolute;
    top: 26px; right: 26px;
    z-index: 2;
    font-size: 1.5rem;
    pointer-events: none;
    transform-origin: 50% 10%;
    filter: drop-shadow(0 4px 9px rgba(0, 0, 0, 0.35));
    animation: cpBellRing 7.5s ease-in-out infinite;
  }
  @keyframes cpBellRing {
    0%, 60%, 100% { transform: rotate(0deg); }
    64% { transform: rotate(17deg); }
    68% { transform: rotate(-14deg); }
    72% { transform: rotate(10deg); }
    76% { transform: rotate(-7deg); }
    80% { transform: rotate(4deg); }
    85% { transform: rotate(-2deg); }
  }

  /* Serial lights — one glow travelling along the row */
  .cp-lights {
    position: absolute;
    left: 0; right: 0; bottom: 12px;
    display: flex;
    justify-content: space-between;
    padding: 0 26px;
    z-index: 2;
    pointer-events: none;
  }
  .cp-light {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.22);
    animation: cpChase 2.8s ease-in-out infinite;
  }
  @keyframes cpChase {
    0%, 62%, 100% {
      background: rgba(253, 230, 138, 0.22);
      box-shadow: none;
      transform: scale(1);
    }
    31% {
      background: #fde68a;
      box-shadow: 0 0 12px rgba(251, 191, 36, 0.95);
      transform: scale(1.55);
    }
  }

  .cp-hero-eyebrow {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 2.4px;
    text-transform: uppercase;
    padding: 6px 16px;
    border-radius: 30px;
    margin-bottom: 14px;
    color: #fff7ed;
    background: rgba(255,255,255,0.14);
    border: 1px solid rgba(255,255,255,0.3);
    backdrop-filter: blur(4px);
  }
  .cp-hero-title {
    font-size: clamp(2.1rem, 5.5vw, 3.4rem);
    letter-spacing: -0.5px;
    background: linear-gradient(100deg, #fff 20%, #fde68a 42%, #fff 62%);
    background-size: 220% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: cpShine 6s linear infinite;
  }
  @keyframes cpShine {
    to { background-position: 220% center; }
  }
  .cp-hero-text {
    font-size: clamp(0.95rem, 2.5vw, 1.1rem);
    opacity: 0.92;
    max-width: 640px;
    margin-left: auto;
    margin-right: auto;
  }

  /* Hero stat chips */
  .cp-chip {
    font-size: 0.84rem;
    font-weight: 700;
    padding: 8px 16px;
    border-radius: 30px;
    border: 1px solid rgba(255,255,255,0.28);
    backdrop-filter: blur(4px);
    transition: transform 0.2s ease;
  }
  .cp-chip:hover { transform: translateY(-3px); }
  .cp-chip-gold  { background: rgba(251,191,36,0.92); color: #451a03; border-color: rgba(255,255,255,0.5); }
  .cp-chip-green { background: rgba(34,197,94,0.92); color: #052e16; border-color: rgba(255,255,255,0.5); }
  .cp-chip-light { background: rgba(255,255,255,0.16); color: #fff; }

  /* ===== Toolbar ===== */
  .cp-toolbar {
    border-radius: 18px;
    position: sticky;
    top: 76px;
    z-index: 5;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(254, 215, 170, 0.8) !important;
  }
  .cp-search {
    border-radius: 50px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #f1e4d0;
  }
  .cp-search .form-control {
    padding: 10px 12px;
    box-shadow: none !important;
  }
  .cp-search-clear {
    color: #9ca3af;
    text-decoration: none;
    padding: 0 14px;
  }
  .cp-search-clear:hover { color: #ea580c; }
  .cp-sort {
    border-radius: 50px;
    padding: 10px 16px;
    border: 1px solid #f1e4d0;
    box-shadow: none !important;
    cursor: pointer;
  }
  .cp-stock-toggle {
    border-radius: 50px;
    padding: 10px 16px;
    background: #fff;
    color: #7c2d12;
    border: 1px solid #f1e4d0;
    transition: all 0.2s ease;
  }
  .cp-stock-toggle:hover {
    background: #fff7ed;
    color: #7c2d12;
    border-color: #fdba74;
  }
  .cp-stock-toggle.is-on,
  .cp-stock-toggle.is-on:hover {
    background: linear-gradient(120deg, #16a34a, #22c55e);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 18px rgba(34,197,94,0.32);
  }
  .cp-toolbar-foot { border-top: 1px dashed #fed7aa; }
  .cp-reset {
    color: #ea580c;
    text-decoration: none;
    font-weight: 700;
  }

  /* ===== Product cards ===== */
  .cp-card {
    border-radius: 20px;
    overflow: hidden;
    background: #fff;
    border: 2px solid transparent !important;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .cp-card:hover {
    transform: translateY(-8px);
    border-color: rgba(234, 88, 12, 0.35) !important;
    box-shadow: 0 24px 44px rgba(194, 65, 12, 0.22) !important;
  }
  .cp-card-out { opacity: 0.82; }
  .cp-card-out .cp-img { filter: grayscale(0.55); }

  .cp-img-wrap {
    position: relative;
    overflow: hidden;
    background: #faf7f2;
  }
  .cp-img {
    height: clamp(190px, 24vw, 240px);
    width: 100%;
    object-fit: cover;
    transition: transform 0.45s ease, filter 0.3s ease;
  }
  .cp-card:hover .cp-img { transform: scale(1.08); }

  /* Diagonal light sweeping across the image on hover */
  .cp-shine {
    position: absolute;
    top: -120%;
    left: -60%;
    width: 45%;
    height: 340%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent);
    transform: rotate(18deg);
    pointer-events: none;
    transition: left 0.7s ease;
  }
  .cp-card:hover .cp-shine { left: 130%; }

  .cp-badge {
    position: absolute;
    top: 12px;
    z-index: 2;
    color: #fff;
    padding: 5px 12px;
    border-radius: 25px;
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 6px 14px rgba(0,0,0,0.18);
  }
  .cp-trending {
    left: 12px;
    background: linear-gradient(135deg, #dc2626, #f97316);
  }
  .cp-discount {
    right: 12px;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
  }
  .cp-soldout {
    position: absolute;
    inset: auto 0 0 0;
    z-index: 2;
    background: rgba(17,24,39,0.78);
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-align: center;
    padding: 7px 0;
  }

  /* Wishlist heart */
  .cp-wish {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 3;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    color: #9ca3af;
    background: rgba(255,255,255,0.94);
    box-shadow: 0 6px 14px rgba(0,0,0,0.16);
    opacity: 0;
    transform: translateY(8px) scale(0.9);
    transition: opacity 0.25s ease, transform 0.25s ease, color 0.2s ease;
  }
  .cp-card:hover .cp-wish { opacity: 1; transform: translateY(0) scale(1); }
  .cp-wish:hover { color: #ef4444; transform: translateY(0) scale(1.14); }
  .cp-wish.is-on {
    opacity: 1;
    color: #ef4444;
    transform: translateY(0) scale(1);
    animation: cpPop 0.4s ease;
  }
  @keyframes cpPop {
    0%   { transform: scale(1); }
    45%  { transform: scale(1.35); }
    100% { transform: scale(1); }
  }

  .cp-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
    background: linear-gradient(to top, rgba(17,24,39,0.55), rgba(17,24,39,0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .cp-card:hover .cp-overlay { opacity: 1; }
  .cp-overlay-btn {
    background: rgba(255,255,255,0.95);
    color: #111827;
    font-weight: 700;
    font-size: 0.82rem;
    padding: 9px 18px;
    border-radius: 50px;
    transform: translateY(10px);
    transition: transform 0.3s ease;
  }
  .cp-card:hover .cp-overlay-btn { transform: translateY(0); }

  .cp-low {
    font-size: 0.7rem;
    font-weight: 800;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 20px;
    padding: 2px 9px;
    white-space: nowrap;
  }
  .cp-name {
    font-size: 1.05rem;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .cp-desc {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 38px;
  }
  .cp-price {
    font-size: 1.35rem;
    background: linear-gradient(120deg, #15803d, #22c55e);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .cp-save {
    color: #16a34a;
    font-weight: 700;
    font-size: 0.76rem;
  }

  .cp-btn, .cp-cta {
    border: none;
    border-radius: 12px;
    color: #fff;
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
    background-size: 180% auto;
    transition: background-position 0.45s ease, transform 0.25s ease, box-shadow 0.25s ease;
  }
  .cp-btn:hover, .cp-btn:focus, .cp-btn:active,
  .cp-cta:hover, .cp-cta:focus, .cp-cta:active {
    background: linear-gradient(120deg, #7c2d12, #ea580c 60%, #f59e0b);
    background-size: 180% auto;
    background-position: right center;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(234, 88, 12, 0.35);
  }
  .cp-cta {
    border-radius: 50px;
    padding: 11px 22px;
    font-weight: 700;
  }
  .cp-btn-arrow {
    display: inline-block;
    transition: transform 0.25s ease;
  }
  .cp-btn:hover .cp-btn-arrow { transform: translateX(5px); }

  /* Empty state */
  .cp-empty { border-radius: 24px; }
  .cp-empty-icon {
    font-size: 3.4rem;
    animation: cpFloat 4s ease-in-out infinite;
  }

  /* Card entrance animation */
  .cp-fade-in {
    animation: cpFadeUp 0.5s ease both;
  }
  @keyframes cpFadeUp {
    from { opacity: 0; transform: translateY(18px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Loading skeletons */
  .cp-skel-card:hover {
    transform: none;
    border-color: transparent !important;
    box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
  }
  .cp-skel {
    background: linear-gradient(90deg, #eee9f5 25%, #f6f1fb 37%, #eee9f5 63%);
    background-size: 400% 100%;
    animation: cpShimmer 1.4s ease infinite;
    border-radius: 8px;
  }
  .cp-skel-img {
    height: clamp(190px, 24vw, 240px);
    width: 100%;
    border-radius: 0;
  }
  .cp-skel-line { height: 12px; margin-bottom: 10px; }
  .cp-skel-stars { width: 45%; }
  .cp-skel-title { width: 85%; height: 16px; }
  .cp-skel-desc  { width: 100%; }
  .cp-skel-price { width: 40%; height: 20px; margin: 14px 0; }
  .cp-skel-btn   { width: 100%; height: 40px; border-radius: 12px; margin-bottom: 0; }
  @keyframes cpShimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (max-width: 991.98px) {
    .cp-toolbar { position: static; }
  }
  @media (max-width: 575.98px) {
    .cp-hero-float { display: none; }
    .cp-wish { opacity: 1; transform: none; }
    /* Thin the garland and lights out so they don't crowd a narrow card */
    .cp-toran-hang:nth-child(even) { display: none; }
    .cp-light:nth-child(even) { display: none; }
    .cp-bell { top: 18px; right: 16px; font-size: 1.2rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .cp-fade-in, .cp-skel, .cp-hero, .cp-hero-blob, .cp-hero-float,
    .cp-hero-title, .cp-empty-icon, .cp-shine,
    .cp-toran-hang, .cp-bell, .cp-light {
      animation: none;
      transition: none;
    }
    .cp-hero-title { -webkit-text-fill-color: #fff; }
  }
`;

export default CategoryProductsPage;
